/**
 * 頁 10 — 執貨清單（`ui-spec` 頁 10）—— **WhatsApp 通知嗰個網址按鈕落嚟嘅頁**
 *
 * 物流／職員照住逐項執：每項顯示批次／到期日（**FIFO 排序**）／倉內可用箱數，
 * 填「實際執到幾箱」＋（執少嘅話）**必填原因**，一按確認 ＝ 同一筆 request 記數 ＋ **即時出帳**。
 *
 * 紅線（同後端一致；前端先擋係為咗好體驗，**後端才係權威**）
 *   · ⚠️ **一定要 `sent` 先准執貨**（通知未發 ＝ 對方唔知有貨）→ 狀態唔啱就唔顯示表單
 *   · ⚠️ 執少**一定要填原因**；⛔ **唔准執多過申報**（要出多 ＝ 另開一張單）
 *   · ⚠️ 要**一次過對齊全部行**（唔准部分執貨）
 *   · ⚠️ 出帳一律後端（`appendMovement`，`OUT`：貨離開物流倉）—— 前端唔碰帳。
 *     ⚠️ 唔係「即刻停計倉租」（期初制）：出貨嗰個週期**照收全額**，由**下一個結算日**
 *     （`settings.fee.settlement_weekday`，`decisions/019` 起 ＝ 逢三）嘅期初 cbm 反映
 *   · ⭐ **執完貨（`acknowledged`）→ 交收一掣就喺呢一頁**（`decisions/018`）：唔准再跳去詳情頁
 *     令倉同事搵唔到掣（2026-09-28 產品負責人實測投訴）
 *
 * ⚠️ 表單住喺 `components/PickForm.tsx`（硬規則 B：頁面檔 ≤200 行）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import OutboundDeliverForm from '../components/OutboundDeliverForm';
import PickForm from '../components/PickForm';
import { OutboundStatusChip, kindLabelKey } from '../components/OutboundStatusChip';
import { btnGhost, card, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type PickList } from '../lib/api';

export default function Picklist() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [data, setData] = useState<PickList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.picklist(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const order = data?.order ?? null;
  /** 可以執貨 ＝ 已通知（`sent`） */
  const canPick = order?.status === 'sent';
  /**
   * ⭐ 執完貨、等交收（`acknowledged`）→ **交收一掣就喺呢一頁**（`decisions/018`；
   *    2026-09-28 產品負責人實測：「i really can't see the button」）。
   *    以前執完之後呢頁只顯示「要『已通知』之後才准執貨」嘅**錯誤提示**（明明已經執完），
   *    掣喺詳情頁最底 —— 倉同事喺現場根本搵唔到。
   */
  const canHandOver = order?.status === 'acknowledged';

  const copyLink = async () => {
    const url = data?.deepLink ?? window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setError(t('out.copyFailed'));
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('out.picklistTitle')}</h2>
        <Link to={`/outbound/${id}`} style={btnGhost}>
          {t('out.backToOrder')}
        </Link>
      </div>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}
        </p>
      )}

      {order && data && (
        <>
          <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '16px' }}>{order.orderNo}</strong>
              <OutboundStatusChip status={order.status} />
              <span style={hintStyle}>{t(kindLabelKey(order.outboundKind))}</span>
            </div>
            <p style={hintStyle}>
              {t('out.pickupAt')}：{order.pickupAt ?? '—'} · {t('out.toLoc')}：{data.toLoc ?? '—'}
            </p>
            {data.deepLink && (
              <p style={hintStyle}>
                {t('out.deepLink')}：{data.deepLink}{' '}
                <button type="button" style={btnGhost} onClick={() => void copyLink()}>
                  {copied ? t('out.copied') : t('out.copyLink')}
                </button>
              </p>
            )}
          </section>

          {canPick ? (
            /* 執完之後**唔跳走**：留喺呢一頁立即見到「✓ 已執貨 ＋ 交收一掣」 */
            <PickForm data={data} onDone={() => void load()} />
          ) : canHandOver && order ? (
            <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
              <p style={{ ...hintStyle, fontWeight: 800, color: 'var(--color-primary)' }}>{t('out.pickDone')}</p>
              <OutboundDeliverForm orderId={order.id} kind={order.outboundKind} mode={order.deliveryMode} onDone={load} />
              <p style={{ marginTop: '10px' }}>
                <Link to={`/outbound/${order.id}`} style={btnGhost}>
                  {t('out.backToOrder')}
                </Link>
              </p>
            </section>
          ) : (
            <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
              <p style={{ ...hintStyle, fontWeight: 700 }}>
                {order?.status === 'draft' ? t('out.pickOnlyWhenSent') : t('out.pickClosed')}
              </p>
              <Link to={`/outbound/${id}`} style={btnGhost}>
                {t('out.backToOrder')}
              </Link>
            </section>
          )}
        </>
      )}
    </>
  );
}
